import { formatNumber, truncateNumbers } from '@kwenta/sdk/utils'
import { wei } from '@synthetixio/wei'
import dynamic from 'next/dynamic'
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import PencilButton from 'components/Button/PencilButton'
import ExtendedModal from 'components/ExtendedModal'
import NumericInput from 'components/Input/NumericInput'
import { FlexDiv, FlexDivCentered, FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import Pill from 'components/Pill'
import Spacer from 'components/Spacer'
import { Body, NumericValue } from 'components/Text'
import { DEFAULT_CRYPTO_DECIMALS } from 'constants/defaults'
import { EXTERNAL_LINKS } from 'constants/links'
import useWindowSize from 'hooks/useWindowSize'
import { setSelectedManageModalType, setSelectedSwapDepositToken } from 'state/futures/reducer'
import {
	selectIsSubmittingCrossTransfer as selectIsSubmittingSmartTransfer,
	selectManageModalType,
} from 'state/futures/selectors'
import {
	approveSmartMargin,
	depositSmartMargin,
	withdrawSmartMargin,
} from 'state/futures/smartMargin/actions'
import {
	selectApprovingSwapDeposit,
	selectMaxSwapToken,
	selectSelectedSwapDepositToken,
	selectSwapDepositAllowance,
	selectSwapDepositBalance,
	selectSwapDepositSlippage,
	selectWithdrawableSmartMargin,
} from 'state/futures/smartMargin/selectors'
import { ManageModalType } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { ExternalLink } from 'styles/common'
import media from 'styles/media'

import DelegationTradingTab, { DelegationTab } from '../DelegationTrading/DelegationTradingTab'
import SwapSlippageSelect from '../SwapSlippageSelect'
import SwapDepositTokenSelector from '../TradeSmartMargin/SwapDepositTokenSelector'

import OneClickTradingTab from './OneClickTrading/OneClickTradingTab'

type Props = {
	defaultTab?: DelegationTab
	onDismiss(): void
}

const SocketBridge = dynamic(() => import('../../../components/SocketBridge'), {
	ssr: false,
})

const PLACEHOLDER = '0.00'

type DepositInputProps = {
	value: string
	setValue(value: string): void
}

const DepositInput: FC<DepositInputProps> = ({ value, setValue }) => {
	return (
		<DepositInputContainer>
			<DepositInputMain placeholder="0.00" value={value} onChange={(_, v) => setValue(v)} />
			<SwapDepositTokenSelector small={false} />
		</DepositInputContainer>
	)
}

type DepositTabProps = {
	extra?: React.ReactNode
}

export const DepositTab: FC<DepositTabProps> = ({ extra }) => {
	const { t } = useTranslation()
	const [amount, setAmount] = useState('')
	const [slippageControlVisible, toggleSlippageControl] = useReducer((o) => !o, false)

	const dispatch = useAppDispatch()

	const balance = useAppSelector(selectSwapDepositBalance)
	const allowance = useAppSelector(selectSwapDepositAllowance)
	const submitting = useAppSelector(selectIsSubmittingSmartTransfer)
	const approving = useAppSelector(selectApprovingSwapDeposit)
	const swapDepositSlippage = useAppSelector(selectSwapDepositSlippage)
	const selectedToken = useAppSelector(selectSelectedSwapDepositToken)

	const needsApproval = useMemo(() => {
		const amtWei = wei(amount || 0)
		return allowance.eq(0) || amtWei.gt(allowance)
	}, [amount, allowance])

	const isDisabled = useMemo(() => {
		if (balance.eq(0)) {
			return true
		} else if (needsApproval) {
			return approving
		} else {
			const amtWei = wei(amount || 0)
			return submitting || amtWei.eq(0) || amtWei.gt(balance)
		}
	}, [amount, submitting, balance, needsApproval, approving])

	const handleSubmit = useCallback(() => {
		if (needsApproval) {
			dispatch(approveSmartMargin())
		} else {
			dispatch(depositSmartMargin(wei(amount)))
		}
	}, [dispatch, amount, needsApproval])

	const buttonKey = useMemo(() => {
		return needsApproval ? 'approve' : approving ? 'approving' : 'deposit'
	}, [needsApproval, approving])

	const handleSetMax = useCallback(() => {
		setAmount(truncateNumbers(balance, DEFAULT_CRYPTO_DECIMALS))
	}, [balance])

	return (
		<>
			<BalanceContainer>
				<BalanceText>{t('futures.market.trade.margin.modal.deposit.amount')}:</BalanceText>
				<BalanceText>
					{t('futures.market.trade.margin.modal.balance')}:{' '}
					<NumericValue size="small" as="span" onClick={handleSetMax} style={{ cursor: 'pointer' }}>
						{formatNumber(balance)}
					</NumericValue>
				</BalanceText>
			</BalanceContainer>

			<DepositInput value={amount} setValue={setAmount} />
			<Spacer height={20} />
			<Spacer height={5} />
			{selectedToken !== 'SUSD' && (
				<>
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Body color="secondary">
							{t('futures.market.trade.margin.modal.deposit.max-slippage')}:
						</Body>
						<FlexDivRowCentered>
							<Body mono>{`${swapDepositSlippage}%`}</Body>
							<PencilButton width={10} height={10} onClick={toggleSlippageControl} />
						</FlexDivRowCentered>
					</div>
					<Spacer height={20} />
				</>
			)}

			{slippageControlVisible && selectedToken !== 'SUSD' && <SwapSlippageSelect />}
			<MinimumAmountDisclaimer>
				{t('futures.market.trade.margin.modal.deposit.disclaimer')}
			</MinimumAmountDisclaimer>
			<Spacer height={!!extra ? 5 : 20} />
			{extra && (
				<>
					{extra}
					<Spacer height={20} />
				</>
			)}
			<TransactionButton
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={isDisabled}
				fullWidth
				onClick={handleSubmit}
				variant="flat"
			>
				{t(`futures.market.trade.margin.modal.buttons.${buttonKey}`)}
			</TransactionButton>
		</>
	)
}

const WithdrawTab = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const [amount, setAmount] = useState('')
	const totalWithdrawable = useAppSelector(selectWithdrawableSmartMargin)
	const submitting = useAppSelector(selectIsSubmittingSmartTransfer)

	const handleSetMax = useCallback(() => {
		setAmount(truncateNumbers(totalWithdrawable, DEFAULT_CRYPTO_DECIMALS))
	}, [totalWithdrawable])

	const isDisabled = useMemo(() => {
		const amtWei = wei(amount || 0)
		return submitting || amtWei.eq(0) || amtWei.gt(totalWithdrawable)
	}, [amount, submitting, totalWithdrawable])

	const handleWithdraw = useCallback(() => {
		dispatch(withdrawSmartMargin(wei(amount)))
	}, [amount, dispatch])

	return (
		<>
			<BalanceContainer>
				<BalanceText>Amount</BalanceText>
				<BalanceText>
					{t('futures.market.trade.margin.modal.balance')}:{' '}
					<NumericValue size="small" as="span" style={{ cursor: 'pointer' }} onClick={handleSetMax}>
						{formatNumber(totalWithdrawable)}
					</NumericValue>
				</BalanceText>
			</BalanceContainer>

			<NumericInput
				dataTestId="futures-market-trade-deposit-margin-input"
				placeholder={PLACEHOLDER}
				value={amount}
				onChange={(_, v) => setAmount(v)}
				right={
					<MaxButton onClick={handleSetMax}>{t('futures.market.trade.margin.modal.max')}</MaxButton>
				}
			/>
			<Spacer height={20} />
			<TransactionButton
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={isDisabled}
				fullWidth
				onClick={handleWithdraw}
				variant="flat"
			>
				{t('futures.market.trade.margin.modal.withdraw.button')}
			</TransactionButton>
		</>
	)
}

const BridgeTab = () => {
	return (
		<>
			<SocketBridge />
			<Spacer height={20} />
		</>
	)
}

const TransferSmartMarginModal: React.FC<Props> = ({ defaultTab, onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const maxToken = useAppSelector(selectMaxSwapToken)
	const transferType = useAppSelector(selectManageModalType)

	const setTransferType = useCallback(
		(modalType: ManageModalType) => dispatch(setSelectedManageModalType(modalType)),
		[dispatch]
	)

	useEffect(() => {
		dispatch(setSelectedSwapDepositToken(maxToken))
	}, [dispatch, maxToken])

	const { greaterThanWidth } = useWindowSize()

	const isDesktop = greaterThanWidth('md')

	const ManageMenuComponent = useMemo(() => {
		return isDesktop ? ManageMenuDesktop : ManageMenuMobile
	}, [isDesktop])

	const values: {
		title: string
		value: ManageModalType
		header: string
		suffix?: ReactNode
		component: ReactNode
		href?: string
	}[] = useMemo(() => {
		const list = [
			{
				title: t('futures.modals.manage.deposit-title'),
				header: t('futures.modals.manage.deposit-header'),
				value: ManageModalType.Deposit,
				component: <DepositTab />,
			},
			{
				title: t('futures.modals.manage.withdraw-title'),
				value: ManageModalType.Withdraw,
				header: t('futures.modals.manage.withdraw-header'),
				component: <WithdrawTab />,
			},
			{
				title: t('futures.modals.manage.bridge-title'),
				value: ManageModalType.Bridge,
				header: t('futures.modals.manage.bridge-header'),
				component: <BridgeTab />,
			},
		]

		const oneclickItems = {
			title: t('futures.modals.manage.one-click-trading-title'),
			value: ManageModalType.OneClick,
			header: t('futures.modals.manage.one-click-trading-header'),
			suffix: <Pill color="lightgreen">{t('futures.modals.manage.new-function')}</Pill>,
			component: <OneClickTradingTab />,
		}

		const delegationItems = {
			title: t('futures.market.trade.margin.modal.delegation.title'),
			value: ManageModalType.Delegation,
			header: t('futures.market.trade.margin.modal.delegation.copy'),
			suffix: <Pill color="lightgreen">{t('futures.modals.manage.new-function')}</Pill>,
			component: <DelegationTradingTab defaultTab={defaultTab || DelegationTab.Delegations} />,
			href: EXTERNAL_LINKS.Docs.DelegationTrading,
		}

		if (isDesktop) {
			list.push(oneclickItems)
			list.push(delegationItems)
		} else {
			list.unshift(oneclickItems)
			list.unshift(delegationItems)
		}

		return list
	}, [defaultTab, isDesktop, t])

	const currentView = useMemo(() => {
		return values.find(({ value }) => value === transferType)
	}, [transferType, values])

	return (
		<StyledExtendedModal isOpen onDismiss={onDismiss}>
			<ManageContent>
				{!isDesktop && <ManageMenuBorder />}
				<ManageMenuComponent>
					{isDesktop && <ManageMenuTitle>{t('futures.modals.manage.title')}</ManageMenuTitle>}
					{values.map(({ title, suffix, value }) => (
						<ManageMenuItemButton
							key={title}
							active={value === transferType}
							onClick={() => setTransferType(value)}
						>
							{title}
							{!!suffix && <span>{suffix}</span>}
						</ManageMenuItemButton>
					))}
				</ManageMenuComponent>
				{currentView && (
					<ManageView>
						<ManageViewTitle>{currentView.title}</ManageViewTitle>
						<Body color="secondary">
							<Trans
								i18nKey={currentView.header}
								components={[<Emphasis href={currentView.href} />]}
							/>
						</Body>
						<Spacer height={20} />
						{currentView.component}
					</ManageView>
				)}
			</ManageContent>
		</StyledExtendedModal>
	)
}

const Emphasis = styled(ExternalLink)`
	color: ${(props) => props.theme.colors.common.primaryYellow};
	cursor: pointer;
`

const ManageMenuDesktop = styled(FlexDivCol)`
	padding: 20px;
	padding-left: 0;
	height: 100%;
	gap: 11.5px;
	border-right: ${(props) => props.theme.colors.selectedTheme.border};
`

const ManageMenuMobile = styled(FlexDivCentered)`
	position: absolute;
	width: calc(100% - 65px);
	overflow-x: auto;
	padding: 12.5px;
	bottom: 0;
	left: 65px;
	gap: 10px;
`

const ManageMenuBorder = styled.div`
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	position: absolute;
	bottom: 65px;
	left: 0;
	right: 0;
	width: 100%;
	height: 1px;
`

const ManageMenuTitle = styled.h3`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.sectionHeader};
	text-transform: uppercase;
	font-family: ${(props) => props.theme.fonts.black};
	font-size: 13px;
	line-height: 1.2;
	margin-bottom: 8.5px;
`

const ManageMenuItemButton = styled.button<{ active?: boolean }>`
	white-space: nowrap;
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	transition: background-color 0.1s ease-in-out;
	background-color: transparent;
	font-size: 15px;
	display: flex;
	width: fit-content;
	align-items: center;
	justify-content: flex-start;
	line-height: 18px;
	font-weight: 700;
	padding: 8.5px 10px;
	border-radius: 100px;
	border: none;
	cursor: pointer;
	gap: 8px;

	&:hover {
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.button.default.background};
	}

	${(props) =>
		props.active &&
		css`
			background-color: ${(props) =>
				props.theme.colors.selectedTheme.newTheme.button.default.hover.background};} !important;
		`};
`

const ManageView = styled(FlexDivCol)`
	padding: 20px 0 20px 20px;

	${media.lessThan('md')`
		padding: 20px 0;
	`}
	width: 100%;
`

const ManageViewTitle = styled.h3`
	font-size: 16px;
	line-height: 16px;
	margin: 0 0 20px 0;
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
`

export const StyledExtendedModal = styled(ExtendedModal)`
	[data-reach-dialog-content] {
		margin-top: 5vh;
		width: 700px;
		height: 700px;

		${media.lessThan('md')`
			margin: 0;
			width: 100%;
			min-height: 100%;
		`}
	}
`

const ManageContent = styled(FlexDiv)`
	background-color: ${(props) => props.theme.colors.selectedTheme.input.background};
	height: 100%;
`

export const BalanceContainer = styled(FlexDivRowCentered)`
	margin-bottom: 8px;
	p {
		margin: 0;
	}
`

export const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

export const MaxButton = styled.button`
	padding: 5px 10px;
	background: ${(props) => props.theme.colors.selectedTheme.button.background};
	border-radius: 15px;
	font-family: ${(props) => props.theme.fonts.mono};
	font-size: 12px;
	line-height: 12px;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	cursor: pointer;
	font-variant: all-small-caps;
`

const MinimumAmountDisclaimer = styled.div`
	padding: 10px;
	border-radius: 8px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.disclaimer.background};
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.disclaimer.color};
	font-size: 13px;
	text-align: center;
`

export const DepositInputMain = styled(NumericInput)`
	border: 0;
	padding: 0;
	height: 23px;
	background: transparent;
	box-shadow: none;

	input {
		font-size: 30px;
		letter-spacing: -1px;
		height: 30px;
		width: 100%;
	}
`

export const DepositInputContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 15px;
	border: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	border-radius: 8px;

	input {
		font-size: 19px;
		font-family: ${(props) => props.theme.fonts.mono};
		color: ${(props) => props.theme.colors.selectedTheme.text.value};
		border: none;
		background-color: transparent;

		&:focus {
			outline: none;
		}
	}
`

export default TransferSmartMarginModal
